// Display queue for realtime 3DS card-validation requests (TT-6783).
//
// Holds the ordered list of validation pop-ups to present, one at a time.
// `queue[0]` is the item currently shown; the rest wait behind it. Ordering is
// by message arrival: a fresh realtime event jumps to the front of the *waiting*
// section (index 1) so the pop-up the user is currently looking at is never
// yanked away. Login-time pending ops are seeded newest-first (`createdTime`
// desc) as the freshness proxy.
//
// This module only carries the lightweight data needed to render the pop-up
// (from the Ably payload). The full record required to actually validate over
// OTP (otp_code_id, accountId, …) lives in `eBanking.pending3ds` and is matched
// back by `acsTransactionId` when the user clicks "Valider".

interface QueueItem {
  acsTransactionId: string | null;
}

interface Validation3dsState {
  queue: QueueItem[];
  // Full challenge records fetched one-by-one via the by-id endpoint (keyed by
  // acsTransactionId), used to drive the OTP flow. The queue itself only holds
  // the lightweight display data from the pub/sub message.
  detailsById: Record<string, unknown>;
  // acsTransactionIds whose pop-up the user closed this session.
  dismissed: Record<string, boolean>;
}

interface EventPayload {
  acs_transaction_id?: string | null;
}

// The pub/sub message is only a trigger carrying the id — the queue holds just
// that. All display and OTP data comes from the by-id endpoint into detailsById.
function normalizeFromEvent(payload?: EventPayload | null): QueueItem {
  return {acsTransactionId: (payload || {}).acs_transaction_id ?? null};
}

function createdTimeMs(value?: string | null): number {
  if (!value) return 0;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

export default {
  namespaced: true,

  state: (): Validation3dsState => ({
    queue: [],
    // Full challenge records fetched one-by-one via the by-id endpoint (keyed by
    // acsTransactionId), used to drive the OTP flow. The queue itself only holds
    // the lightweight display data from the pub/sub message.
    detailsById: {},
    // acsTransactionIds whose pop-up the user closed this session. Kept in memory
    // only (never persisted): a closed pop-up must not re-open while navigating,
    // but should surface again on a full reload / new tab. Map for O(1) lookup.
    dismissed: {},
  }),

  getters: {
    current: (state: Validation3dsState): QueueItem | null => state.queue[0] || null,
    hasPending: (state: Validation3dsState): boolean => state.queue.length > 0,
    pendingCount: (state: Validation3dsState): number => state.queue.length,
    detailsFor:
      (state: Validation3dsState) =>
      (acsTransactionId: string): unknown =>
        state.detailsById[acsTransactionId] || null,
  },

  mutations: {
    // Insert a fresh request. Ignores duplicates (by acsTransactionId) so a
    // realtime event and its REST refetch never double-queue. An empty queue
    // makes the item active (shown immediately); otherwise it goes to the front
    // of the waiting section, leaving the currently-shown pop-up (index 0) put.
    enqueueFront(state: Validation3dsState, item: QueueItem) {
      if (!item?.acsTransactionId) return;
      // A pop-up the user closed this session stays closed until reload.
      if (state.dismissed[item.acsTransactionId]) return;
      if (state.queue.some((q) => q.acsTransactionId === item.acsTransactionId)) {
        return;
      }
      if (state.queue.length === 0) {
        state.queue.push(item);
      } else {
        state.queue.splice(1, 0, item);
      }
    },

    // Seed from the login-time pending list. Preserves anything already queued
    // (including an open pop-up) and appends the not-yet-known items in the
    // order given, de-duplicated by acsTransactionId.
    seed(state: Validation3dsState, items: QueueItem[]) {
      const seen = new Set<string>();
      const merged: QueueItem[] = [];
      for (const it of [...state.queue, ...(items || [])]) {
        if (!it?.acsTransactionId || seen.has(it.acsTransactionId)) continue;
        // Don't resurrect a pop-up the user already closed this session.
        if (state.dismissed[it.acsTransactionId]) continue;
        seen.add(it.acsTransactionId);
        merged.push(it);
      }
      state.queue = merged;
    },

    setDetails(
      state: Validation3dsState,
      {acsTransactionId, data}: {acsTransactionId: string; data: unknown},
    ) {
      if (!acsTransactionId) return;
      state.detailsById = {...state.detailsById, [acsTransactionId]: data};
    },

    dequeueCurrent(state: Validation3dsState) {
      const removed = state.queue.shift();
      if (removed?.acsTransactionId && state.detailsById[removed.acsTransactionId]) {
        const rest = {...state.detailsById};
        delete rest[removed.acsTransactionId];
        state.detailsById = rest;
      }
    },

    remove(state: Validation3dsState, acsTransactionId: string) {
      state.queue = state.queue.filter(
        (q) => q.acsTransactionId !== acsTransactionId,
      );
      if (state.detailsById[acsTransactionId]) {
        const rest = {...state.detailsById};
        delete rest[acsTransactionId];
        state.detailsById = rest;
      }
    },

    clear(state: Validation3dsState) {
      state.queue = [];
      state.detailsById = {};
    },

    // Mark a request's pop-up as closed for this session (postpone). It won't
    // re-open on navigation; a full reload clears `dismissed` and shows it again.
    markDismissed(state: Validation3dsState, acsTransactionId: string) {
      if (!acsTransactionId) return;
      state.dismissed = {...state.dismissed, [acsTransactionId]: true};
    },

    // Clear the session dismissal — used when the user deliberately re-opens the
    // request from the notification center.
    undismiss(state: Validation3dsState, acsTransactionId: string) {
      if (!acsTransactionId || !state.dismissed[acsTransactionId]) return;
      const rest = {...state.dismissed};
      delete rest[acsTransactionId];
      state.dismissed = rest;
    },
  },

  actions: {
    enqueueFromEvent({commit}, payload?: EventPayload | null) {
      commit('enqueueFront', normalizeFromEvent(payload));
    },

    // Fetch the full challenge for one acs_transaction_id (by-id endpoint) and
    // stash it — this is the source of the pop-up's display data and the OTP
    // flow's fields. If it can't be loaded (expired / not found), drop it from
    // the queue so a dead item doesn't block the ones behind it.
    async fetchDetails({commit, dispatch}, acsTransactionId?: string | null) {
      if (!acsTransactionId) return null;
      try {
        const res = await dispatch(
          'eBanking/actionGet3DsById',
          acsTransactionId,
          {root: true},
        );
        if (res?.result) {
          commit('setDetails', {acsTransactionId, data: res.result});
          return res;
        }
      } catch {
        // fall through to remove
      }
      commit('remove', acsTransactionId);
      return null;
    },

    // Case 3: build the queue from the REST pending list on login, newest-first.
    // The list already carries the full challenges, so seed both the ordered ids
    // and their details — no per-id fetch needed.
    seedFromPending({commit, rootState}) {
      const list = rootState.eBanking?.pending3ds;
      if (!Array.isArray(list)) return;
      const sorted = list
        .filter((i) => i?.acsTransactionId)
        .slice()
        .sort((a, b) => createdTimeMs(b.createdTime) - createdTimeMs(a.createdTime));
      commit(
        'seed',
        sorted.map((i) => ({acsTransactionId: i.acsTransactionId})),
      );
      sorted.forEach((i) =>
        commit('setDetails', {acsTransactionId: i.acsTransactionId, data: i}),
      );
    },
  },
};
